<script setup>
  import { Settings, MessageSquare } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { Switch } from "@/components/ui/switch";
  import { Label } from "@/components/ui/label";
  import ThreePanePage from "@/templates/ThreePanePage.vue";
  import CardsPane from "@/components/Survey/Components/CardsList/CardsPane.vue";
  import ReviewPane from "@/components/Survey/Review/ReviewPane/ReviewPane.vue";
  import ReviewCommentsPanel from "@/components/Survey/Review/Collaboration/ReviewCommentsPanel.vue";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useChannel } from "@composables/useChannel";
  import { storeToRefs } from "pinia";
  import { useEventBus } from "@vueuse/core";
  import { provide, nextTick, onMounted, onUnmounted, ref } from "vue";

  const showOptions = ref(true);
  const showAnswers = ref(true);
  const showConditions = ref(true);

  const surveyCardsStore = useSurveyCardsStore();
  const { searchQuery, presenceOfCard } = storeToRefs(surveyCardsStore);
  const { selectCurrentCard, updateSearchQuery } = surveyCardsStore;

  const currentCardStore = useCurrentCardStore();
  const surveyStore = useSurveyStore();
  const reviewStore = useReviewCommentsStore();
  reviewStore.reset();

  const reviewChannel = useChannel("ReviewCommentsChannel", {
    received: (message) => reviewStore.handleChannelMessage(message),
  });

  const props = defineProps({
    cards: { type: Array, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    cardTypesGroups: { type: Array, required: true },
    isCardFilteredOut: { type: Function, required: true },
    isFilterActive: { type: Boolean, required: true },
    questionCount: { type: Number, required: true },
    switchBetweenTabs: { type: Boolean, required: true },
  });

  const cardsPane = ref(null);

  const selectAndscrollToReviewCard = async ({ token }) => {
    selectCurrentCard({ token });
    await scrollToReviewCard({ token });
  };

  const scrollToReviewCard = async ({ token }) => {
    await nextTick();
    const card = document.getElementById(`review-card-${token}`);
    const reviewPaneContainer = document.getElementById("review-pane-container");
    if (card && reviewPaneContainer) {
      // Get the card's position relative to the scroll container
      const containerRect = reviewPaneContainer.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();
      const scrollTop = reviewPaneContainer.scrollTop;
      const targetPosition = scrollTop + cardRect.top - containerRect.top - 10;

      reviewPaneContainer.scrollTo({
        top: targetPosition,
        behavior: "smooth",
      });

      card.classList.add("review-card-focused");
      window.setTimeout(() => card.classList.remove("review-card-focused"), 1500);
    }
  };

  const scrollToCard = async () => {
    await nextTick();

    const targetCard = document.getElementById(`nav-menu-item-${currentCardStore.currentToken}`);
    if (targetCard && cardsPane.value?.cardList?.scrollbarRef) {
      cardsPane.value.cardList.scrollbarRef.setScrollTop(targetCard.offsetTop - 10);
    }
  };

  onMounted(() => {
    reviewStore.fetchThreads();
    reviewChannel.subscribe({ survey_token: surveyStore.token });
    if (props.switchBetweenTabs) scrollToCard();
  });

  onUnmounted(() => reviewChannel.unsubscribe());

  const reviewEventBus = useEventBus("review");
  provide("reviewEventBus", reviewEventBus);
  reviewEventBus.on((eventDescriptor) => {
    const { command, ...event } = eventDescriptor;
    const actions = {
      select: selectAndscrollToReviewCard,
      scroll: scrollToReviewCard,
      search: updateSearchQuery,
    };
    actions[command]?.(event);
  });
</script>

<template>
  <ThreePanePage
    :span-left="3"
    :span-center="8"
    :span-right="1"
    data-cy="survey-questions-review-page">
    <template #left>
      <CardsPane
        ref="cardsPane"
        class="h-full"
        :card-sub-types-infos="cardSubTypesInfos"
        :cards="cards"
        event-bus-name="reviewEventBus"
        :is-card-filtered-out="isCardFilteredOut"
        :is-filter-active="isFilterActive"
        :is-footer-hidden="true"
        :is-list-read-only="true"
        :question-count="questionCount"
        :search-query="searchQuery"
        :selected-card-token="currentCardStore.currentToken" />
    </template>
    <template #center>
      <div class="relative flex-1 h-full">
        <div
          id="review-pane-container"
          class="h-full overflow-y-auto rounded-lg bg-background">
          <ReviewPane
            v-if="presenceOfCard"
            id="review-pane"
            class="flex-1"
            :card-sub-types-infos="cardSubTypesInfos"
            :card-types-groups="cardTypesGroups"
            :cards="cards"
            :show-options="showOptions"
            :show-answers="showAnswers"
            :show-conditions="showConditions" />
        </div>

        <Transition
          enter-active-class="transition-all duration-200 ease-out"
          leave-active-class="transition-all duration-150 ease-in"
          enter-from-class="opacity-0 translate-x-4"
          leave-to-class="opacity-0 translate-x-4">
          <ReviewCommentsPanel v-if="reviewStore.panel.open" />
        </Transition>
      </div>
    </template>
    <template #right>
      <div
        v-if="presenceOfCard"
        class="flex flex-col items-center gap-2">
        <Popover>
          <PopoverTrigger as-child>
            <Button
              variant="outline"
              size="icon"
              class="h-9 w-9 shadow-sm">
              <Settings class="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            side="left"
            align="start"
            class="w-56">
            <div class="space-y-4">
              <p class="text-sm font-medium">Display options</p>
              <div class="space-y-3">
                <div class="flex items-center justify-between">
                  <Label
                    for="show-answers"
                    class="text-sm font-normal cursor-pointer"
                    >Answers</Label
                  >
                  <Switch
                    id="show-answers"
                    v-model="showAnswers" />
                </div>
                <div class="flex items-center justify-between">
                  <Label
                    for="show-conditions"
                    class="text-sm font-normal cursor-pointer"
                    >Conditions</Label
                  >
                  <Switch
                    id="show-conditions"
                    v-model="showConditions" />
                </div>
                <div class="flex items-center justify-between">
                  <Label
                    for="show-options"
                    class="text-sm font-normal cursor-pointer"
                    >Options</Label
                  >
                  <Switch
                    id="show-options"
                    v-model="showOptions" />
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
        <Tooltip content="All comments">
          <Button
            variant="outline"
            size="icon"
            class="h-9 w-9 shadow-sm relative"
            data-comment-trigger
            @click="reviewStore.openAllComments()">
            <MessageSquare class="h-4 w-4" />
            <span
              v-if="reviewStore.totalOpenCount > 0"
              class="absolute -top-1 -right-1 h-4 min-w-4 px-0.5 rounded-full bg-status-info text-white text-3xs flex items-center justify-center"
              >{{ reviewStore.totalOpenCount }}</span
            >
          </Button>
        </Tooltip>
      </div>
    </template>
  </ThreePanePage>
</template>
